import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Package, Search, ArrowLeft, MapPin, Clock, CheckCircle2, Truck, AlertCircle, Calendar, Box } from 'lucide-react';
import { BrandMark } from '../components/BrandMark';
import axios from 'axios';
import TrackingMap from '../components/TrackingMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  BOOKED: { icon: Calendar, color: '#52525B', label: 'BOOKED' },
  PAID: { icon: CheckCircle2, color: '#00C853', label: 'PAID' },
  SCHEDULED: { icon: Calendar, color: '#FFB800', label: 'PICKUP SCHEDULED' },
  PICKED_UP: { icon: Box, color: '#FFB800', label: 'PICKED UP' },
  IN_TRANSIT: { icon: Truck, color: '#FF4500', label: 'IN TRANSIT' },
  OUT_FOR_DELIVERY: { icon: Truck, color: '#FF4500', label: 'OUT FOR DELIVERY' },
  DELIVERED: { icon: CheckCircle2, color: '#00C853', label: 'DELIVERED' },
  DELAYED: { icon: AlertCircle, color: '#DC2626', label: 'DELAYED' }
};

const STATUS_ORDER = ['BOOKED', 'PAID', 'SCHEDULED', 'PICKED_UP', 'IN_TRANSIT', 'OUT_FOR_DELIVERY', 'DELIVERED'];

const Track = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryTag = searchParams.get('tag');
  
  const [searchInput, setSearchInput] = useState(queryTag || '');
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (queryTag) {
      handleTrack(queryTag);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryTag]);

  const handleTrack = async (input) => {
    const searchValue = input || searchInput;
    if (!searchValue.trim()) {
      setError('Please enter a baggage tag or booking ID');
      return;
    }

    setLoading(true);
    setError('');
    setTracking(null);

    try {
      const response = await axios.get(`${API}/track/${searchValue.trim()}`);
      setTracking(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'No tracking information found');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleTrack();
  };

  const getStatusIndex = (status) => STATUS_ORDER.indexOf(status);

  const isStatusComplete = (status, currentStatus) => {
    if (currentStatus === 'DELAYED') return false;
    return getStatusIndex(currentStatus) >= getStatusIndex(status);
  };

  return (
    <div className="theme-sunset p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <BrandMark size={36} />
          </div>
          <button
            data-testid="back-home-btn"
            onClick={() => navigate(localStorage.getItem('user_id') ? '/dashboard' : '/')}
            className="btn-secondary px-4 py-2 flex items-center gap-2 text-sm"
          >
            <ArrowLeft size={16} />
            {localStorage.getItem('user_id') ? 'DASHBOARD' : 'HOME'}
          </button>
        </div>

        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-black uppercase mb-2" style={{ fontFamily: 'Cabinet Grotesk', lineHeight: '1' }}>
            TRACK LUGGAGE
          </h1>
          <p className="text-sm text-gray-600 font-mono uppercase tracking-wide">REAL-TIME SHIPMENT STATUS</p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSubmit} className="mb-8" data-testid="track-form">
          <div className="flex flex-col md:flex-row gap-4">
            <input
              data-testid="track-input"
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Enter Baggage Tag (e.g. LFJ123456) or Booking ID"
              className="flex-1"
            />
            <button
              data-testid="track-search-btn"
              type="submit"
              disabled={loading}
              className="btn-primary px-8 py-4 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Search size={20} />
              {loading ? 'TRACKING...' : 'TRACK'}
            </button>
          </div>
          {error && (
            <p data-testid="track-error" className="text-red-600 text-sm mt-4 font-mono p-4 border-2 border-red-600 bg-red-50">
              {error}
            </p>
          )}
        </form>

        {/* Tracking Results */}
        {tracking && (
          <div data-testid="tracking-results">
            {/* Summary Card */}
            <div className="card-brutalist p-6 mb-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div>
                  <div className="data-label mb-1">BAGGAGE TAG</div>
                  <div className="text-3xl font-black font-mono" data-testid="tracking-tag">{tracking.baggage_tag}</div>
                </div>
                <div className="flex items-center gap-3 px-4 py-2 border-2 border-black" style={{ backgroundColor: STATUS_CONFIG[tracking.tracking_status]?.color, color: '#000' }}>
                  {(() => {
                    const Icon = STATUS_CONFIG[tracking.tracking_status]?.icon || Clock;
                    return <Icon size={20} />;
                  })()}
                  <span className="font-bold uppercase text-sm" data-testid="tracking-status">
                    {STATUS_CONFIG[tracking.tracking_status]?.label || tracking.tracking_status}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t-2 border-dashed border-gray-300">
                <div>
                  <div className="data-label mb-1">CUSTOMER</div>
                  <div className="data-value text-sm">{tracking.customer_name}</div>
                </div>
                <div>
                  <div className="data-label mb-1">ROUTE</div>
                  <div className="data-value text-sm">{tracking.pickup_location || 'N/A'} → {tracking.delivery_location}</div>
                </div>
                <div>
                  <div className="data-label mb-1">PIECES</div>
                  <div className="data-value text-sm">{tracking.luggage_details.quantity}</div>
                </div>
                <div>
                  <div className="data-label mb-1">WEIGHT</div>
                  <div className="data-value text-sm">{tracking.luggage_details.weight} KG</div>
                </div>
              </div>
            </div>

            {/* Live Map */}
            <div className="mb-6">
              <TrackingMap
                bookingId={tracking.booking_id}
                pickupLocation={tracking.pickup_location}
                deliveryLocation={tracking.delivery_location}
                trackingStatus={tracking.tracking_status}
                pickupAddress={tracking.pickup_address}
                deliveryAddress={tracking.delivery_address}
              />
            </div>

            {/* Progress Tracker */}
            {tracking.tracking_status !== 'DELAYED' && (
              <div className="card-brutalist p-6 mb-6">
                <h2 className="text-lg font-bold uppercase mb-6 pb-2 border-b-2 border-black" style={{ fontFamily: 'IBM Plex Mono' }}>
                  PROGRESS
                </h2>
                <div className="flex flex-wrap items-center gap-2">
                  {STATUS_ORDER.map((status, index) => {
                    const isComplete = isStatusComplete(status, tracking.tracking_status);
                    const isCurrent = tracking.tracking_status === status;
                    const StatusIcon = STATUS_CONFIG[status]?.icon || Clock;
                    return (
                      <React.Fragment key={status}>
                        <div className="flex flex-col items-center gap-2 min-w-[80px]">
                          <div
                            className={`w-12 h-12 border-2 border-black flex items-center justify-center ${isComplete ? '' : 'opacity-30'}`}
                            style={{ backgroundColor: isComplete ? STATUS_CONFIG[status].color : '#fff' }}
                          >
                            <StatusIcon size={20} color={isComplete ? '#000' : '#666'} />
                          </div>
                          <div className={`text-[10px] font-mono uppercase text-center max-w-[80px] leading-tight ${isCurrent ? 'font-bold' : ''}`}>
                            {STATUS_CONFIG[status].label}
                          </div>
                        </div>
                        {index < STATUS_ORDER.length - 1 && (
                          <div className={`flex-1 h-0.5 min-w-[10px] ${getStatusIndex(tracking.tracking_status) > index ? 'bg-black' : 'bg-gray-300'}`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tracking History Timeline */}
            <div className="card-brutalist p-6">
              <h2 className="text-lg font-bold uppercase mb-6 pb-2 border-b-2 border-black" style={{ fontFamily: 'IBM Plex Mono' }}>
                TRACKING HISTORY
              </h2>
              <div className="space-y-4" data-testid="tracking-history">
                {tracking.tracking_history && tracking.tracking_history.length > 0 ? (
                  [...tracking.tracking_history].reverse().map((entry, index) => {
                    const StatusIcon = STATUS_CONFIG[entry.status]?.icon || Clock;
                    const isLatest = index === 0;
                    return (
                      <div key={`${entry.status}-${entry.timestamp || entry.updated_at || index}`} className={`flex gap-4 p-4 border-l-4 ${isLatest ? 'border-l-orange-600 bg-orange-50' : 'border-l-gray-300'}`}>
                        <div className="flex-shrink-0">
                          <div
                            className="w-10 h-10 border-2 border-black flex items-center justify-center"
                            style={{ backgroundColor: STATUS_CONFIG[entry.status]?.color || '#fff' }}
                          >
                            <StatusIcon size={18} color="#000" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-1">
                            <div className="font-bold uppercase font-mono text-sm">
                              {STATUS_CONFIG[entry.status]?.label || entry.status}
                            </div>
                            <div className="text-xs text-gray-600 font-mono">
                              {new Date(entry.timestamp).toLocaleString('en-IN', {
                                day: '2-digit', month: 'short', year: 'numeric',
                                hour: '2-digit', minute: '2-digit'
                              })}
                            </div>
                          </div>
                          {entry.location && (
                            <div className="text-xs text-gray-600 flex items-center gap-1 mb-1">
                              <MapPin size={12} />
                              {entry.location}
                            </div>
                          )}
                          {entry.notes && (
                            <div className="text-sm text-gray-700 mt-1">{entry.notes}</div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-600 font-mono text-sm">No tracking history available.</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Track;
